import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { WEB_ADVISOR_LOGO, fileUrl } from "@/lib/products";
import { PLAN_STATUS_STYLES, EngagementBadge } from "@/components/webadvisor/PlanActions";

const STATUSES = ["New Lead", "Contacted", "Proposal Sent", "Negotiating", "Won", "Lost"];
const scoreColor = (s) => (s >= 80 ? "text-green-600" : s >= 50 ? "text-yellow-600" : "text-red-600");
const today = () => new Date().toISOString().slice(0, 10);
const isDue = (l) => l.follow_up_date && l.follow_up_date <= today() && !["Won", "Lost"].includes(l.status);

const StageSelect = ({ lead, onUpdate, suffix = "" }) => (
  <Select value={lead.status} onValueChange={v => onUpdate(lead.id, { status: v })}>
    <SelectTrigger className="w-full md:w-36 h-9 md:h-8 text-xs" data-testid={`lead-status-${lead.id}${suffix}`}><SelectValue /></SelectTrigger>
    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
  </Select>
);

const RepCell = ({ lead, reps, isAdmin, onUpdate, suffix = "" }) => isAdmin ? (
  <Select value={lead.assigned_to || ""} onValueChange={v => onUpdate(lead.id, { assigned_to: v })}>
    <SelectTrigger className="w-full md:w-36 h-9 md:h-8 text-xs" data-testid={`lead-assign-${lead.id}${suffix}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
    <SelectContent>{reps.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
  </Select>
) : <span className="text-sm text-slate-700">{lead.assigned_name || "You"}</span>;

const FollowUp = ({ lead, onUpdate, suffix = "" }) => {
  const overdue = lead.follow_up_date && lead.follow_up_date < today() && !["Won", "Lost"].includes(lead.status);
  return (
    <Input type="date" key={lead.follow_up_date || "none"} defaultValue={lead.follow_up_date || ""}
      onBlur={e => { const v = e.target.value || null; if (v !== (lead.follow_up_date || null)) onUpdate(lead.id, { follow_up_date: v }); }}
      className={`h-9 md:h-8 text-xs w-full md:w-36 ${overdue ? "border-red-400 text-red-600" : ""}`} data-testid={`lead-followup-${lead.id}${suffix}`} />
  );
};

const PlanCell = ({ lead, suffix = "" }) => (
  <div className="flex flex-col gap-1 items-start">
    {lead.plan_status ? <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${PLAN_STATUS_STYLES[lead.plan_status]}`} data-testid={`lead-plan-status-${lead.id}${suffix}`}>{lead.plan_status}{lead.plan_sent_at ? " · sent" : ""}</span> : <span className="text-xs text-slate-400">No plan</span>}
    {lead.plan_sent_at && <EngagementBadge item={lead} testId={`lead-engagement-${lead.id}${suffix}`} />}
  </div>
);

const Prospect = ({ lead, onOpen, suffix = "" }) => (
  <button type="button" onClick={() => onOpen(lead)} className="flex items-center gap-2 min-w-0 text-left hover:opacity-80" data-testid={`lead-manage-${lead.id}${suffix}`}>
    {lead.logo_url && <img src={fileUrl(lead.logo_url)} alt="" className="w-8 h-8 object-contain rounded shrink-0" />}
    <div className="min-w-0">
      <p className="font-semibold text-slate-900 truncate">{lead.business_name || lead.name || (lead.website_url || "").replace(/^https?:\/\//, "")}</p>
      <p className="text-xs text-slate-400 truncate">{lead.email || lead.website_url}</p>
      {lead.source?.startsWith("SEO:") && <p className="text-[11px] font-semibold text-green-700 truncate" data-testid={`lead-source-${lead.id}`}>{lead.source}</p>}
    </div>
  </button>
);

const LeadRow = ({ lead, reps, isAdmin, onUpdate, onOpen }) => (
  <TableRow data-testid={`lead-row-${lead.id}`}>
    <TableCell className="min-w-[180px] max-w-[240px]"><Prospect lead={lead} onOpen={onOpen} /></TableCell>
    <TableCell className={`font-black text-lg ${scoreColor(lead.website_score)}`} data-testid={`lead-score-${lead.id}`}>{lead.audit_id ? lead.website_score : "—"}</TableCell>
    <TableCell><PlanCell lead={lead} /></TableCell>
    <TableCell><StageSelect lead={lead} onUpdate={onUpdate} /></TableCell>
    <TableCell><RepCell lead={lead} reps={reps} isAdmin={isAdmin} onUpdate={onUpdate} /></TableCell>
    <TableCell><FollowUp lead={lead} onUpdate={onUpdate} /></TableCell>
    <TableCell>
      {lead.audit_id && <Link to={`/webadvisor/audit/${lead.audit_id}`} className="text-blue-600 hover:text-blue-500 text-sm font-medium flex items-center gap-1" data-testid={`lead-open-${lead.id}`}>Report<ExternalLink className="w-3.5 h-3.5" /></Link>}
    </TableCell>
  </TableRow>
);

const LeadCard = ({ lead, reps, isAdmin, onUpdate, onOpen }) => (
  <div className={`bg-white border rounded-xl p-4 shadow-sm space-y-3 ${isDue(lead) ? "border-red-300" : "border-slate-200"}`} data-testid={`lead-card-${lead.id}`}>
    <div className="flex items-start justify-between gap-3">
      <Prospect lead={lead} onOpen={onOpen} suffix="-m" />
      <span className={`font-black text-2xl leading-none ${scoreColor(lead.website_score)}`}>{lead.audit_id ? lead.website_score : "—"}</span>
    </div>
    <PlanCell lead={lead} suffix="-m" />
    <div className="grid grid-cols-2 gap-2">
      <div><p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Stage</p><StageSelect lead={lead} onUpdate={onUpdate} suffix="-m" /></div>
      <div><p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Follow-up</p><FollowUp lead={lead} onUpdate={onUpdate} suffix="-m" /></div>
    </div>
    <div className="flex items-center justify-between gap-2">
      <div className="flex-1 min-w-0"><RepCell lead={lead} reps={reps} isAdmin={isAdmin} onUpdate={onUpdate} suffix="-m" /></div>
      {lead.audit_id && <Link to={`/webadvisor/audit/${lead.audit_id}`} className="text-blue-600 text-sm font-medium flex items-center gap-1 shrink-0" data-testid={`lead-open-${lead.id}-m`}>Report<ExternalLink className="w-3.5 h-3.5" /></Link>}
    </div>
  </div>
);

export default function WebAdvisorLeadsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [leads, setLeads] = useState(null);
  const [reps, setReps] = useState([]);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const setOpenLead = (l) => navigate(`/webadvisor/leads/${l.id}`);

  const load = useCallback(() => api.get("/api/webadvisor/leads").then(r => setLeads(r.data)).catch(() => setLeads([])), []);
  useEffect(() => {
    load();
    if (isAdmin) api.get("/api/webadvisor/reps").then(r => setReps(r.data)).catch(() => {});
  }, [load, isAdmin]);

  const onUpdate = async (id, patch) => {
    try {
      const { data } = await api.put(`/api/webadvisor/leads/${id}`, patch);
      setLeads(ls => ls.map(l => (l.id === id ? { ...l, ...data } : l)));
      toast.success("Lead updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const all = leads || [];
  const shown = all.filter(l => filter === "all" || (filter === "due" ? isDue(l) : filter === "viewed" ? l.view_count > 0 : l.status === filter));
  const counts = { due: all.filter(isDue).length, viewed: all.filter(l => l.view_count > 0).length };
  const label = (f) => (f === "all" ? `All (${all.length})` : f === "due" ? `Follow-up due (${counts.due})` : f === "viewed" ? `Viewed proposal (${counts.viewed})` : f);

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16 px-4 sm:px-6" data-testid="webadvisor-leads-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <img src={WEB_ADVISOR_LOGO} alt="Web Advisor AI" className="h-14 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-black text-slate-900">Prospect Pipeline</h1>
              <p className="text-sm text-slate-500">{isAdmin ? "Every scanned prospect across your team" : "Prospects assigned to you"}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
            {isAdmin && <Link to="/webadvisor/prospects" className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold" data-testid="import-leads-btn">Import prospects</Link>}
            <Link to="/webadvisor" className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2.5 rounded-lg" data-testid="leads-new-scan-btn">New Scan</Link>
          </div>
        </div>
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="leads-filters">
          {["all", "due", "viewed", ...STATUSES].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid={`leads-filter-${f.replace(/\s+/g, "-").toLowerCase()}`}>
              {label(f)}
            </button>
          ))}
        </div>
        {leads === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
          <>
            <div className="md:hidden space-y-3" data-testid="leads-cards">
              {shown.map(l => <LeadCard key={l.id} lead={l} reps={reps} isAdmin={isAdmin} onUpdate={onUpdate} onOpen={setOpenLead} />)}
              {shown.length === 0 && <p className="text-center text-slate-400 py-10 bg-white border border-slate-200 rounded-xl" data-testid="leads-empty-m">No prospects here yet — run a scan to add one.</p>}
            </div>
            <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="leads-table">
              <Table>
                <TableHeader><TableRow><TableHead>Prospect</TableHead><TableHead>Score</TableHead><TableHead>Plan</TableHead><TableHead>Stage</TableHead><TableHead>Rep</TableHead><TableHead>Follow-up</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {shown.map(l => <LeadRow key={l.id} lead={l} reps={reps} isAdmin={isAdmin} onUpdate={onUpdate} onOpen={setOpenLead} />)}
                  {shown.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="leads-empty">No prospects here yet — run a scan to add one.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
